import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Headphones, Play, Pause, FileText, Loader2, Volume2, Clock } from 'lucide-react';

function AudioPlayer({ src, title, index }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setPlaying(!playing);
  };

  const onTimeUpdate = () => {
    if (!audioRef.current) return;
    setProgress(audioRef.current.currentTime);
    setDuration(audioRef.current.duration || 0);
  };

  const seek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    if (audioRef.current) audioRef.current.currentTime = pct * (audioRef.current.duration || 0);
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-slate-800/60 rounded-lg p-4 border border-slate-700/50" data-testid={`audio-player-${index}`}>
      <audio ref={audioRef} src={src} onTimeUpdate={onTimeUpdate} onEnded={() => setPlaying(false)} onLoadedMetadata={onTimeUpdate} preload="metadata" />
      <div className="flex items-center gap-3">
        <button onClick={toggle} className="w-10 h-10 rounded-full bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center text-cyan-400 hover:bg-cyan-500/30 transition-colors flex-shrink-0" data-testid={`play-btn-${index}`}>
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-300 truncate mb-1.5">{title}</p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 w-10 text-right flex-shrink-0">{fmt(progress)}</span>
            <div className="flex-1 h-1.5 bg-slate-700 rounded-full cursor-pointer relative" onClick={seek}>
              <div className="h-full bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full transition-all" style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }} />
            </div>
            <span className="text-xs text-slate-500 w-10 flex-shrink-0">{fmt(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function DocumentViewer({ content }) {
  if (!content) return null;
  const sections = content.split(/(?=^## )/m).filter(Boolean);
  return (
    <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
      {sections.map((section, i) => {
        const lines = section.split('\n');
        const heading = lines[0]?.replace(/^#+\s*/, '') || `Section ${i + 1}`;
        const body = lines.slice(1).join('\n').trim();
        return (
          <div key={heading} className="bg-slate-800/40 rounded-lg p-4 border border-slate-700/30">
            <h3 className="text-cyan-400 font-semibold text-sm mb-2">{heading}</h3>
            <p className="text-slate-400 text-xs leading-relaxed whitespace-pre-wrap">{body.slice(0, 800)}{body.length > 800 ? '...' : ''}</p>
          </div>
        );
      })}
    </div>
  );
}

export default function Podcast({ apiUrl, embed = false }) {
  const [documents, setDocuments] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docContent, setDocContent] = useState('');
  const [generating, setGenerating] = useState(false);
  const [activeEpisode, setActiveEpisode] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [docsRes, epsRes] = await Promise.all([
        axios.get(`${apiUrl}/podcast/documents`),
        axios.get(`${apiUrl}/podcast/episodes`),
      ]);
      setDocuments(docsRes.data || []);
      setEpisodes(epsRes.data || []);
    } catch (e) {
      console.error('Failed to load podcast data', e);
    }
    setLoading(false);
  };

  const selectDocument = async (doc) => {
    setSelectedDoc(doc);
    try {
      const res = await axios.get(`${apiUrl}/uploads/${doc.filename}`, { responseType: 'text' });
      setDocContent(res.data);
    } catch {
      setDocContent('');
    }
  };

  const generatePodcast = async () => {
    if (!selectedDoc) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${apiUrl}/podcast/generate`, {
        document_path: selectedDoc.filename,
        voice: "onyx",
        speed: 0.95,
      });
      toast.success(`Podcast generation started for "${selectedDoc.title}"`);
      // Poll for completion
      const epId = res.data.id;
      const poll = setInterval(async () => {
        try {
          const check = await axios.get(`${apiUrl}/podcast/episodes/${epId}`);
          if (check.data.status === 'complete') {
            clearInterval(poll);
            setGenerating(false);
            setActiveEpisode(check.data);
            fetchData();
            toast.success('Podcast ready!');
          } else if (check.data.status === 'error') {
            clearInterval(poll);
            setGenerating(false);
            toast.error('Podcast generation failed');
          }
        } catch (pollErr) { console.error('Poll error:', pollErr); }
      }, 5000);
    } catch (e) {
      setGenerating(false);
      toast.error('Failed to start podcast generation');
    }
  };

  const openEpisode = (ep) => {
    setActiveEpisode(ep);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="podcast-page">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Headphones className="w-7 h-7 text-cyan-400" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent" data-testid="podcast-title">
              NextXus Podcast Studio
            </h1>
          </div>
          <p className="text-slate-400 text-sm">Transform the library&apos;s knowledge into podcast episodes. Listen to the architecture of consciousness.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Document Selection & Episodes */}
          <div className="lg:col-span-1 space-y-6">
            {/* Available Documents */}
            <div className="bg-slate-900/60 rounded-xl border border-slate-800/50 p-4">
              <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-cyan-400" />
                Source Documents
              </h2>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {documents.map((doc) => (
                  <button
                    key={doc.filename}
                    onClick={() => selectDocument(doc)}
                    className={`w-full text-left p-2.5 rounded-lg text-xs transition-colors border ${
                      selectedDoc?.filename === doc.filename
                        ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300'
                        : 'bg-slate-800/40 border-slate-700/30 text-slate-400 hover:border-slate-600'
                    }`}
                    data-testid={`doc-${doc.filename}`}
                  >
                    <p className="font-medium truncate">{doc.title}</p>
                    <p className="text-slate-500 mt-0.5">{doc.word_count.toLocaleString()} words</p>
                  </button>
                ))}
                {documents.length === 0 && <p className="text-xs text-slate-500">No markdown documents found</p>}
              </div>
            </div>

            {/* Generate Button */}
            {selectedDoc && (
              <button
                onClick={generatePodcast}
                disabled={generating}
                className="w-full py-3 bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="generate-podcast-btn"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Generating Audio...</>
                ) : (
                  <><Volume2 className="w-4 h-4" /> Generate Podcast</>
                )}
              </button>
            )}

            {/* Episode History */}
            <div className="bg-slate-900/60 rounded-xl border border-slate-800/50 p-4">
              <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <Headphones className="w-4 h-4 text-purple-400" />
                Episodes ({episodes.length})
              </h2>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {episodes.map((ep) => (
                  <button
                    key={ep.id}
                    onClick={() => openEpisode(ep)}
                    className={`w-full text-left p-2.5 rounded-lg text-xs transition-colors border ${
                      activeEpisode?.id === ep.id
                        ? 'bg-purple-500/10 border-purple-500/30 text-purple-300'
                        : 'bg-slate-800/40 border-slate-700/30 text-slate-400 hover:border-slate-600'
                    }`}
                    data-testid={`episode-${ep.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-medium truncate flex-1">{ep.source_document}</p>
                      <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] ${
                        ep.status === 'complete' ? 'bg-green-500/20 text-green-400' :
                        ep.status === 'generating' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>{ep.status}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-slate-500">
                      <Clock className="w-3 h-3" />
                      <span>{ep.section_count || 0} sections</span>
                      <span>{new Date(ep.created_at).toLocaleDateString()}</span>
                    </div>
                  </button>
                ))}
                {episodes.length === 0 && <p className="text-xs text-slate-500">No episodes yet. Select a document and generate.</p>}
              </div>
            </div>
          </div>

          {/* Right: Content & Player */}
          <div className="lg:col-span-2 space-y-6">
            {/* Active Episode Player */}
            {activeEpisode && activeEpisode.status === 'complete' && (
              <div className="bg-slate-900/60 rounded-xl border border-slate-800/50 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center">
                    <Headphones className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-base font-semibold text-white">Now Playing</h2>
                    <p className="text-xs text-slate-400">{activeEpisode.source_document} - {activeEpisode.sections?.length || 0} sections</p>
                  </div>
                </div>

                {activeEpisode.full_episode && (
                  <div className="mb-4 bg-gradient-to-r from-cyan-950/40 to-purple-950/40 rounded-lg p-4 border border-cyan-800/30" data-testid="full-episode-player">
                    <p className="text-xs font-semibold text-cyan-400 mb-2">Full Episode ({activeEpisode.total_words?.toLocaleString() || '?'} words)</p>
                    <AudioPlayer
                      src={`${apiUrl}/uploads/${activeEpisode.full_episode}`}
                      title="Full Episode — All Sections"
                      index="full"
                    />
                  </div>
                )}

                <p className="text-xs text-slate-500 mb-3">Individual Sections:</p>
                <div className="space-y-3">
                  {(activeEpisode.sections || []).map((section, idx) => (
                    <AudioPlayer
                      key={section.title || section.audio_file}
                      src={`${apiUrl}/uploads/${section.audio_file}`}
                      title={section.title}
                      index={idx}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Generating Status */}
            {generating && (
              <div className="bg-slate-900/60 rounded-xl border border-cyan-500/20 p-6 text-center">
                <Loader2 className="w-10 h-10 text-cyan-400 animate-spin mx-auto mb-3" />
                <h3 className="text-base font-semibold text-cyan-300">Generating Podcast</h3>
                <p className="text-xs text-slate-400 mt-1">Converting document to HD audio. This may take a few minutes for large documents.</p>
              </div>
            )}

            {/* Document Preview */}
            {selectedDoc && docContent && !activeEpisode && !generating && (
              <div className="bg-slate-900/60 rounded-xl border border-slate-800/50 p-5">
                <h2 className="text-base font-semibold text-white mb-1">{selectedDoc.title}</h2>
                <p className="text-xs text-slate-500 mb-4">{selectedDoc.word_count.toLocaleString()} words - Preview</p>
                <DocumentViewer content={docContent} />
              </div>
            )}

            {/* Empty State */}
            {!selectedDoc && !activeEpisode && !generating && (
              <div className="bg-slate-900/60 rounded-xl border border-slate-800/50 p-12 text-center">
                <Headphones className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                <h3 className="text-base font-semibold text-slate-400">Select a Document</h3>
                <p className="text-xs text-slate-500 mt-1">Choose a source document from the library to preview and generate a podcast episode.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
